import React, { useState } from 'react';
import { supabase } from '../../../supabaseClient';
import { useAdmin } from '../../../context/AdminContext';

// ─── Edit Player Sub-Modal ────────────────────────────────────────────────────
const JERSEY_SIZES = ['S', 'M', 'L', 'XL', '2XL', '3XL'];

const EditPlayerModal = ({ player, onSave, onClose }) => (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-[70]">
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-md w-full space-y-4 shadow-xl">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {player.isNew ? 'Add Player' : 'Edit Player'}
            </h2>
            {[
                { label: 'Player Name', key: 'name',          type: 'text',   placeholder: 'Player Name' },
                { label: 'Age',         key: 'age',           type: 'number', placeholder: 'Age' },
                { label: 'Jersey #',    key: 'jersey_number', type: 'number', placeholder: 'Jersey Number' },
            ].map(({ label, key, type, placeholder }) => (
                <input
                    key={key}
                    type={type}
                    value={player[key] ?? ''}
                    onChange={e => onSave({ ...player, [key]: e.target.value }, true /* draft */)}
                    placeholder={placeholder}
                    className="w-full border dark:border-gray-500 p-2 rounded
                               bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400"
                />
            ))}
            <select
                value={player.jersey_size ?? 'L'}
                onChange={e => onSave({ ...player, jersey_size: e.target.value }, true)}
                className="w-full border dark:border-gray-500 p-2 rounded
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
                {JERSEY_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <label className="flex items-center gap-2 text-gray-800 dark:text-gray-200 cursor-pointer">
                <input
                    type="checkbox"
                    checked={player.is_muslim ?? true}
                    onChange={e => onSave({ ...player, is_muslim: e.target.checked }, true)}
                />
                Muslim Player
            </label>
            <div className="flex justify-end gap-2 pt-2">
                <button onClick={onClose}
                    className="px-4 py-2 bg-gray-300 dark:bg-gray-600 dark:text-white rounded">
                    Cancel
                </button>
                <button onClick={() => onSave(player, false)}
                    className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700">
                    Save
                </button>
            </div>
        </div>
    </div>
);

// ─── Registration Roster Modal ────────────────────────────────────────────────
export const RegistrationRosterModal = ({ registration, players, onClose, onRefresh }) => {
    const { adminId } = useAdmin();
    const [editingPlayer, setEditingPlayer] = useState(null);

    const BLANK = { name: '', age: '', is_muslim: true, jersey_number: '', jersey_size: 'L', isNew: true };

    const handleSave = async (player, isDraft) => {
        if (isDraft) { setEditingPlayer(player); return; }
        await supabase.functions.invoke('admin-actions', {
            body: { action: 'savePlayer', adminId, payload: { player, registrationId: registration.id } },
        });
        await onRefresh();
        setEditingPlayer(null);
    };

    const handleDelete = async (playerId) => {
        if (!window.confirm('Delete this player?')) return;
        await supabase.functions.invoke('admin-actions', {
            body: { action: 'deletePlayer', adminId, payload: { playerId } },
        });
        await onRefresh();
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-lg w-full shadow-xl">
                <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">
                    Roster — {registration.team_name}
                </h2>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                    {players.map(p => (
                        <div key={p.id} className="border dark:border-gray-600 p-3 rounded flex justify-between items-center">
                            <div className="text-gray-800 dark:text-gray-200 text-sm">
                                <p><strong>{p.name}</strong> — Age {p.age}</p>
                                <p>Jersey #{p.jersey_number} · Size {p.jersey_size}</p>
                                <p>{p.is_muslim ? 'Muslim' : 'Non-Muslim'}</p>
                            </div>
                            <div className="flex flex-col gap-2">
                                <button onClick={() => setEditingPlayer({ ...p, isNew: false })}
                                    className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700">Edit</button>
                                <button onClick={() => handleDelete(p.id)}
                                    className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700">Delete</button>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="flex gap-2 mt-4">
                    <button onClick={() => setEditingPlayer({ ...BLANK })}
                        className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700">
                        + Add Player
                    </button>
                    <button onClick={onClose}
                        className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700">
                        Close
                    </button>
                </div>
            </div>
            {editingPlayer && (
                <EditPlayerModal player={editingPlayer} onSave={handleSave} onClose={() => setEditingPlayer(null)} />
            )}
        </div>
    );
};

// ─── Team Roster Modal ────────────────────────────────────────────────────────
export const TeamRosterModal = ({ team, players, onClose, onRefresh }) => {
    const { adminId, activeSeason } = useAdmin();
    const [editingPlayer, setEditingPlayer] = useState(null);

    const BLANK = { name: '', age: '', is_muslim: true, jersey_number: '', jersey_size: 'L', isNew: true };

    const handleSave = async (player, isDraft) => {
        if (isDraft) { setEditingPlayer(player); return; }
        await supabase.functions.invoke('admin-actions', {
            body: { action: 'saveTeamPlayer', adminId, payload: { player, teamId: team.id, seasonId: activeSeason?.id } },
        });
        await onRefresh();
        setEditingPlayer(null);
    };

    const handleDelete = async (playerId) => {
        if (!window.confirm('Delete this player?')) return;
        await supabase.functions.invoke('admin-actions', {
            body: { action: 'deleteTeamPlayer', adminId, payload: { playerId } },
        });
        await onRefresh();
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-lg w-full shadow-xl">
                <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">
                    Team Roster — {team.name}
                </h2>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                    {players.map(p => (
                        <div key={p.id} className="border dark:border-gray-600 p-3 rounded flex justify-between items-center">
                            <div className="text-gray-800 dark:text-gray-200 text-sm">
                                <p><strong>{p.name}</strong> — Age {p.age}</p>
                                <p>Jersey #{p.jersey_number} · Size {p.jersey_size}</p>
                                <p>{p.is_muslim ? 'Muslim' : 'Non-Muslim'}</p>
                            </div>
                            <div className="flex flex-col gap-2">
                                <button onClick={() => setEditingPlayer({ ...p, isNew: false })}
                                    className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700">Edit</button>
                                <button onClick={() => handleDelete(p.id)}
                                    className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700">Delete</button>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="flex gap-2 mt-4">
                    <button onClick={() => setEditingPlayer({ ...BLANK })}
                        className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700">
                        + Add Player
                    </button>
                    <button onClick={onClose}
                        className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700">
                        Close
                    </button>
                </div>
            </div>
            {editingPlayer && (
                <EditPlayerModal player={editingPlayer} onSave={handleSave} onClose={() => setEditingPlayer(null)} />
            )}
        </div>
    );
};